import { QR_BASE } from '../config/constants';

const isActiveBottle = (b) => {
  return b?.status === 'in_stock';
};

export const deriveProductsFromBottles = (bottles, skuMap, brand) => {
  const groups = {};
  Object.entries(bottles || {}).forEach(([bottleId, b]) => {
    if (!b || b.brand !== brand) return;
    const sku = b.sku || '';
    const location = b.location || '';
    const groupKey = `${brand}|${sku}|${location}`;
    if (!groups[groupKey]) {
      const skuInfo = (skuMap || {})[sku] || {};
      // Fallback name when SKU is not in /sku/ map
      const displayName = skuInfo.wine_name
        || b.wine_name
        || (sku ? `不明商品 (SKU: ${sku})` : `不明商品 (${bottleId})`);
      groups[groupKey] = {
        id: groupKey,
        _groupKey: groupKey,
        _bottleIds: [],
        sku,
        location,
        brand,
        type: b.type || '',
        name: displayName,
        vintage: skuInfo.vintage || b.vintage || '',
        winery: skuInfo.winery_id || b.winery_id || '',
        stock: 0,
        cost: b.cost_price || 0,
        price: b.sale_price || 0,
        intake_date: '',
        created_at: '',
        addedDate: '',
        lastSold: '',
        lastStockCheck: '',
      };
    }
    if (isActiveBottle(b)) groups[groupKey].stock += 1;
    // Use most recent bottle's cost_price (by intake_date or created_at)
    if (b.cost_price != null) {
      const bDate = b.intake_date || b.created_at || '';
      const gDate = groups[groupKey]._latestCostDate || '';
      if (bDate >= gDate) {
        groups[groupKey].cost = b.cost_price;
        groups[groupKey]._latestCostDate = bDate;
      }
    }
    if (b.sale_price != null) groups[groupKey].price = b.sale_price;
    if (b.type) groups[groupKey].type = b.type;
    groups[groupKey]._bottleIds.push(bottleId);
    // Track latest activity dates across bottles in group
    const g = groups[groupKey];
    if (b.intake_date && b.intake_date > (g.intake_date || '')) g.intake_date = b.intake_date;
    if (b.created_at && b.created_at > (g.created_at || '')) g.created_at = b.created_at;
    if (b.sold_at && b.sold_at > (g.lastSold || '')) g.lastSold = b.sold_at;
    if (b.added_date && b.added_date > (g.addedDate || '')) g.addedDate = b.added_date;
  });
  return Object.values(groups);
};

// V4 Bottle ID: random 6-char from legible charset (no 0/O, 1/I/L, U/V)
const BOTTLE_ID_CHARS = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';
export const generateBottleId = (existingIds) => {
  const existing = existingIds instanceof Set ? existingIds : new Set(Object.keys(existingIds || {}));
  for (let attempt = 0; attempt < 100; attempt++) {
    let id = '';
    for (let i = 0; i < 6; i++) {
      id += BOTTLE_ID_CHARS[Math.floor(Math.random() * BOTTLE_ID_CHARS.length)];
    }
    if (!existing.has(id)) return id;
  }
  // Fallback: timestamp-based
  return Date.now().toString(36).toUpperCase().slice(-6);
};

export const applyProductsDeltaToBottles = (prevBottles, prevProducts, nextProducts, brand) => {
  const nextBottles = { ...(prevBottles || {}) };
  const prevMap = new Map((prevProducts || []).map(p => [p._groupKey || p.id, p]));
  const nextMap = new Map((nextProducts || []).map(p => [p._groupKey || p.id, p]));
  const existingIds = new Set(Object.keys(nextBottles));

  // Remove groups
  prevMap.forEach((prevP, key) => {
    if (!nextMap.has(key)) {
      (prevP._bottleIds || []).forEach(id => {
        if (nextBottles[id]) nextBottles[id] = { ...nextBottles[id], status: 'sold' };
      });
    }
  });

  // Add/update groups
  nextMap.forEach((nextP, key) => {
    const prevP = prevMap.get(key);
    const targetStock = Number(nextP.stock || 0);
    const prevStock = prevP ? Number(prevP.stock || 0) : 0;
    const sku = nextP.sku || (prevP && prevP.sku) || '';
    const location = nextP.location || (prevP && prevP.location) || '';
    const type = nextP.type || (prevP && prevP.type) || '';
    const cost = Number(nextP.cost || 0);
    const price = Number(nextP.price || 0);
    // Update existing bottles fields
    const bottleIds = (prevP && prevP._bottleIds) || [];
    bottleIds.forEach(id => {
      const b = nextBottles[id];
      if (!b) return;
      const updated = {
        ...b,
        sku,
        brand,
        location,
        type,
        cost_price: cost,
        sale_price: price,
      };
      nextBottles[id] = updated;
    });

    if (targetStock > prevStock) {
      const addCount = targetStock - prevStock;
      for (let i = 0; i < addCount; i += 1) {
        const bottleId = generateBottleId(existingIds);
        existingIds.add(bottleId);
        const newBottle = {
          sku,
          brand,
          stock_id: bottleId,
          old_sku: '',
          location,
          status: 'in_stock',
          owner_customer_id: null,
          cost_price: cost,
          sale_price: price,
          qr_url: `${QR_BASE}${bottleId}`,
          type,
        };
        nextBottles[bottleId] = newBottle;
      }
    } else if (targetStock < prevStock) {
      const removeCount = prevStock - targetStock;
      let removed = 0;
      for (const id of bottleIds) {
        if (removed >= removeCount) break;
        const b = nextBottles[id];
        if (b && isActiveBottle(b)) {
          nextBottles[id] = { ...b, status: 'sold' };
          removed += 1;
        }
      }
    }
  });

  return nextBottles;
};
